import { proxy, useSnapshot } from 'valtio';
import { enumChecker } from '@soundxyz/utils/const';
import { RefetchOperationsOnComplete } from '@graphql/helpers/effects';
import type {
  LikesActivityFeedHookQuery,
  PlaylistActivityFeedHookQuery,
  PlaylistHookQuery,
} from '@graphql/reactQuery';
import {
  AddReleaseToPlaylistDocument,
  DeleteShelfDocument,
  PlaylistHookDocument,
  PlaylistPageNewDocument,
  SetShelfReleasesDocument,
} from '@graphql/reactQuery';

export const PAGE_SIZE = 15;
export const INTERSECTION_OBSERVER_ROOT_MARGIN = '-45% -5px';

export type PlaylistActivityGroup = NonNullable<
  NonNullable<PlaylistActivityFeedHookQuery['playlistActivityFeed']>['edges'][number]['node']
>;

export type LikesActivityGroup = NonNullable<
  NonNullable<LikesActivityFeedHookQuery['likeActivityFeed']>['edges'][number]['node']
>;

export type PlaylistRelease = NonNullable<
  PlaylistHookQuery['shelf']
>['orderedReleases']['edges'][number]['node'];

export const TAB_CHOICES = {
  SOUNDS: 'sounds',
  ACTIVITY: 'activity',
} as const;

export type TabChoice = (typeof TAB_CHOICES)[keyof typeof TAB_CHOICES];

export const isTabChoice = enumChecker(TAB_CHOICES);

RefetchOperationsOnComplete({
  trigger: [SetShelfReleasesDocument, DeleteShelfDocument, AddReleaseToPlaylistDocument],
  refetch: [PlaylistHookDocument, PlaylistPageNewDocument],
});

export const PlaylistsState = proxy<{
  [playlistId: string]: {
    orderedReleaseIds: {
      date?: string[];
      index?: string[];
    };
    orderedTrackIds: {
      date?: string[];
      index?: string[];
    };
    scrollIndex?: number;
    activeMintableReleaseIndex: number | null;
  };
}>({});

const emptyPlaylistsState: (typeof PlaylistsState)[string] = {
  orderedReleaseIds: {},
  orderedTrackIds: {},
  activeMintableReleaseIndex: null,
};

export function usePlaylistState({ playlistId }: { playlistId: string }) {
  return useSnapshot(PlaylistsState)[playlistId] || emptyPlaylistsState;
}

export function setPlaylistOrderedsIds({
  playlistId,
  releaseIds,
  trackIds,
  orderType,
}: {
  playlistId: string;
  orderType: 'date' | 'index';

  releaseIds?: string[];
  trackIds?: string[];
}) {
  PlaylistsState[playlistId] ||= {
    orderedReleaseIds: {},
    orderedTrackIds: {},
    activeMintableReleaseIndex: null,
  };
  const state = PlaylistsState[playlistId];

  if (!state) return; // This never really happens

  if (releaseIds) {
    state.orderedReleaseIds[orderType] = releaseIds;
  }

  if (trackIds) {
    state.orderedTrackIds[orderType] = trackIds;
  }
}

export function optimisticSetPlaylistOrderedsIds({
  playlistId,
  releaseIds,
  orderType,
}: {
  playlistId: string;
  orderType: 'date' | 'index';

  releaseIds: string[];
}) {
  PlaylistsState[playlistId] ||= {
    orderedReleaseIds: {},
    orderedTrackIds: {},
    activeMintableReleaseIndex: null,
  };
  const state = PlaylistsState[playlistId];

  if (!state) return; // This never really happens

  const previousReleaseIds = state.orderedReleaseIds[orderType];

  state.orderedReleaseIds[orderType] = releaseIds;

  return {
    rollback() {
      state.orderedReleaseIds[orderType] = previousReleaseIds;
    },
  };
}

export function setPlaylistScrollIndex({
  playlistId,
  scrollIndex,
}: {
  playlistId: string;
  scrollIndex: number;
}) {
  PlaylistsState[playlistId] ||= {
    orderedReleaseIds: {},
    orderedTrackIds: {},
    activeMintableReleaseIndex: null,
  };
  const state = PlaylistsState[playlistId];

  if (!state) return; // This never really happens

  state.scrollIndex = scrollIndex;
}

export function setActiveMintableReleaseIndex({
  playlistId,
  index,
}: {
  playlistId: string;
  index: number | null;
}) {
  PlaylistsState[playlistId] ||= {
    orderedReleaseIds: {},
    orderedTrackIds: {},
    activeMintableReleaseIndex: null,
  };
  const state = PlaylistsState[playlistId];

  if (!state) return; // This never really happens

  state.activeMintableReleaseIndex = index;
}
